import { appStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { updateShowWelcomeChannelAction } from '../../home/actions/HomeActions';
import { UserSettingsActions } from '../actions/UserSettingsActions';
import { IEditDisplayNameControls } from '../directives/tbEditDisplayName';
import { IEditEmailControls } from '../directives/tbEditEmail';
import { FeatureFlags } from '../FeatureFlags';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { Module } from '../Module';
import { ServerSettings } from '../resources/ServerSettings';
import { UserSettings } from '../resources/UserSettings';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ModalService } from './ModalService';
import { ServerService } from './ServerService';
import { DefaultAuthenticationProviderId } from './SiteAuthenticationSettingsService';
import { StartPageService } from './StartPageService';
import { ToasterService, ToastType } from './ToasterService';

export class UserSettingsCtrlCommon {

  public static $inject = [
    '$q',
    '$state',
    '$translate',
    'ServerService',
    'ServerSettings',
    'StartPageService',
    'UserSettings',
    'UserSettingsActions',
    'ModalService',
    'ToasterService'
  ];

  constructor(
    private $q: ng.IQService,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    private serverService: ServerService,
    private serverSettings: ServerSettings,
    private startPageService: StartPageService,
    private userSettingsService: UserSettings,
    private userSettingsActions: UserSettingsActions,
    private modal: ModalService,
    private toaster: ToasterService
  ) {}

  private scope: UserSettingsCtrlCommon.Scope;
  private boundOnAppStateChanged = this.onAppStateChanged.bind(this);

  public initScope(
    scope: UserSettingsCtrlCommon.Scope,
    isViewerSelf: boolean,
    serverUser: IServerUserInfo,
    siteUser: ISiteUserInfo,
    userSettings: IUserSettings,
    userRefreshTokenCount: number): void {

    this.scope = scope;

    // setup change listener for user settings
    this.setupUserSettingsChangeListener();

    scope.server = this.serverService;

    scope.isDomainFamilyAD = this.serverService.isServerDomainFamilyActiveDirectory();
    scope.isDomainFamilyExternal = this.serverService.isServerDomainFamilyExternal();
    scope.isDomainFamilyLocal = this.serverService.isServerDomainFamilyLocal();

    scope.isViewerSelf = isViewerSelf;
    scope.isViewerServerAdmin = this.serverService.isSessionServerAdmin();
    const isViewerAdmin = this.serverService.isSessionSiteOrServerAdmin();
    scope.isViewerAdmin = isViewerAdmin;

    scope.changePasswordUrl = this.serverService.changePasswordUrl(); // might be undefined

    scope.updateDisplayNameAllowed = this.serverService.updateDisplayNameAllowed();

    scope.controls = {
      editDisplayNameControls: null,
      editEmailControls: null
    };

    scope.userSettings = userSettings || {};
    scope.serverUser = serverUser;

    // section: username, display name, email

    scope.disableNameAndEmailSaveButton = () => {
      const displayCtrl = scope.controls.editDisplayNameControls;
      const emailCtrl = scope.controls.editEmailControls;

      // if neither exists, disable
      if (!displayCtrl && !emailCtrl) {
        return true;
      }

      // if any controls is busy, disable
      if (displayCtrl && displayCtrl.isBusy()) {
        return true;
      }
      if (emailCtrl && emailCtrl.isBusy()) {
        return true;
      }

      // if no control is dirty, disable
      if (!emailCtrl && displayCtrl && !displayCtrl.isDirty()) {
        return true;
      }
      if (!displayCtrl && emailCtrl && !emailCtrl.isDirty()) {
        return true;
      }
      if (displayCtrl && !displayCtrl.isDirty() && emailCtrl && !emailCtrl.isDirty()) {
        return true;
      }

      // if any control is dirty but invalid, disable
      if (displayCtrl && displayCtrl.isDirty() && !displayCtrl.isValid()) {
        return true;
      }
      if (emailCtrl && emailCtrl.isDirty() && !emailCtrl.isValid()) {
        return true;
      }

      // no reason to disable the button
      return false;
    };

    scope.saveChangesToEmailAndDisplayName = () => {
      const displayCtrl = scope.controls.editDisplayNameControls;
      const emailCtrl = scope.controls.editEmailControls;
      // When the site user authentication provider is non-default, the username is the email address
      const userNameIsSameAsEmail = !!siteUser && !!siteUser.authenticationProviderId && siteUser.authenticationProviderId !== DefaultAuthenticationProviderId;

      let displayPromise: ng.IPromise<any> = this.$q.when({});
      if (displayCtrl && displayCtrl.isDirty()) {
        displayPromise = displayCtrl.submit(serverUser.id, serverUser.username);
      }
      let emailPromise: ng.IPromise<any> = this.$q.when({});
      if (emailCtrl && emailCtrl.isDirty()) {
        emailPromise = emailCtrl.submit(serverUser.id, serverUser.username);
        // If username is tied to email address, update the username in the application state to allow proper page
        // refresh/reload
        if(userNameIsSameAsEmail){
          emailPromise = emailPromise.then((result) => {
            this.$state.params['username'] = result;
          });
        }
      }

      // reload state once display name and email update requests are done
      this.$q.all([displayPromise, emailPromise]).then(() => this.$state.hardReload());
    };

    // TODO: most of this logic should be encapsulated elsewhere, like in the StartPageService
    scope.updateUserStartPage = (startPage: string) => {
      // Show server default start page if not set by user
      if (!startPage) {
        // Start page from sessionInfo
        startPage = this.serverService.getStartPage();
      }

      const currentSiteUrlName = this.serverService.sessionSiteUrlName();
      scope.startPageText = startPage;
      scope.startPageHref = this.startPageService.getStartPageForSite(startPage, currentSiteUrlName);
    };

    scope.updateUserStartPage(scope.userSettings.startPage);

    //Section: Refresh Failure Notification Setting
    scope.siteUser = siteUser;
    scope.origNotificationSetting = siteUser && siteUser.notificationEnabled;

    // settings checkboxes and save buttons are disabled if the user does not have a record on the current site
    // this occurs when server admins attempt to change their site-specific settings without having content on that site
    scope.userHasNoSiteId = () => {
      return (!siteUser || !siteUser.id);
    };

    scope.isNotificationEnabledForServer =
      this.serverService.isNotificationEnabledForServer();
    scope.isNotificationEnabledForSite =
      (!scope.userHasNoSiteId()) && this.serverService.isNotificationEnabledForSite();

    // the save button is disabled if the current value of the checkbox is different from the value saved in server
    scope.isUpdateNotificationSettingDisabled = () => {
      return (scope.userHasNoSiteId() || scope.origNotificationSetting === siteUser.notificationEnabled);
    };

    scope.updateNotificationSettingBusy = false;
    scope.updateNotificationSetting = () => {
      scope.updateNotificationSettingBusy = true;
      this.userSettingsService.updateNotificationSetting(
        siteUser.id, siteUser.notificationEnabled)
        .then((result: IResult) => {
          this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
          scope.origNotificationSetting = siteUser.notificationEnabled;
          scope.updateNotificationSettingBusy = false;
        }).catch((error: IError) => this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()}));
    };

    scope.initialWelcomeChannelEnabled = this.serverService.isWelcomeChannelEnabledForUser();
    scope.userSettings.welcomeChannelEnabled = this.serverService.isWelcomeChannelEnabledForUser();
    scope.isWelcomeChannelEnabledPristine = () => {
      return userSettings.welcomeChannelEnabled === scope.initialWelcomeChannelEnabled;
    };
    scope.updateWelcomeChannelEnabledBusy = false;
    scope.updateWelcomeChannelEnabled = () => {
      scope.updateWelcomeChannelEnabledBusy = true;
      this.userSettingsService.updateWelcomeChannelEnabled(scope.userSettings.welcomeChannelEnabled)
        .then(() => {
          this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess') });
          scope.updateWelcomeChannelEnabledBusy = false;
          scope.initialWelcomeChannelEnabled = userSettings.welcomeChannelEnabled;
          // Update the redux store so we don't need to make a getSessionInfoCall
          const updateWelcomeChannelAction = updateShowWelcomeChannelAction(scope.userSettings.welcomeChannelEnabled);
          appStore.dispatch(updateWelcomeChannelAction);
        }).catch(() => this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() }));
    };

    // Suspended Subscription Notification section
    if (FeatureFlags.suspendSubscriptions) {
      scope.isSuspendedSubscriptionVisible = () => {
        return isViewerAdmin && scope.isViewerSelf;
      };
      if (scope.isSuspendedSubscriptionVisible()) {
        // get the user's preference saved in server
        this.userSettingsService.getSuspendedSubscriptionSetting(this.scope.serverUser.id)
        .then((result: boolean) => {
          scope.origSuspendedSubscriptionSetting = result;
          scope.userSettings.suspendedSubscriptionSetting = result;
        }).catch((error: IError) => { });

        scope.isSuspendedSubscriptionSettingBusy = false;

        // update the value saved in server
        scope.updateSuspendedSubscriptionSetting = () => {
          // disable the save button while waiting for response
          scope.isSuspendedSubscriptionSettingBusy = true;

          this.userSettingsService.updateSuspendedSubscriptionSetting(
            siteUser.id,
            scope.userSettings.suspendedSubscriptionSetting
          ).then((result: IResult) => {
            this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
            scope.origSuspendedSubscriptionSetting = scope.userSettings.suspendedSubscriptionSetting;
            scope.isSuspendedSubscriptionSettingBusy = false;
          }).catch((error: IError) => {
            scope.isSuspendedDataAlertSettingBusy = false;
            this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
          });
        };

        // the save button is disabled if the current value of the checkbox is different from the value saved in server
        scope.isSaveSuspendedSubscriptionSettingDisabled = () => {
          return scope.userHasNoSiteId() || scope.origSuspendedSubscriptionSetting === scope.userSettings.suspendedSubscriptionSetting;
        };
      }
    }

    // Suspended Data Alert Notification section
    if (FeatureFlags.suspendDataAlerts) {
      scope.isSuspendedDataAlertVisible = () => {
        return isViewerAdmin && scope.isViewerSelf;
      };
      if (scope.isSuspendedDataAlertVisible()) {
        // get the user's preference saved in server
        this.userSettingsService.getSuspendedDataAlertSetting(this.scope.serverUser.id)
        .then((result: boolean) => {
          scope.origSuspendedDataAlertSetting = result;
          scope.userSettings.suspendedDataAlertSetting = result;
        }).catch((error: IError) => { });

        scope.isSuspendedDataAlertSettingBusy = false;

        // update the value saved in server
        scope.updateSuspendedDataAlertSetting = () => {
          // disable the save button while waiting for response
          scope.isSuspendedDataAlertSettingBusy = true;

          this.userSettingsService.updateSuspendedDataAlertSetting(
            siteUser.id,
            scope.userSettings.suspendedDataAlertSetting
          ).then((result: IResult) => {
            this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
            scope.origSuspendedDataAlertSetting = scope.userSettings.suspendedDataAlertSetting;
            scope.isSuspendedDataAlertSettingBusy = false;
          }).catch((error: IError) =>  {
            scope.isSuspendedDataAlertSettingBusy = false;
            this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
          });
        };

        // the save button is disabled if the current value of the checkbox is different from the value saved in server
        scope.isSaveSuspendedDataAlertSettingDisabled = () => {
          return scope.userHasNoSiteId() || scope.origSuspendedDataAlertSetting === scope.userSettings.suspendedDataAlertSetting;
        };
      }
    }

    if (FeatureFlags.markAnimationServer) {
      scope.isMarkAnimationVisible = () => {
        return scope.isViewerSelf;
      };
      if (scope.isMarkAnimationVisible()) {
        // get the user's preference saved in server
        this.userSettingsService.getMarkAnimationSetting(this.scope.serverUser.id)
        .then((result: boolean) => {
          scope.origMarkAnimationSetting = result;
          scope.userSettings.markAnimationSetting = result;
        }).catch((error: IError) => { });

        scope.isMarkAnimationSettingBusy = false;

        // update the value saved in server
        scope.updateMarkAnimationSetting = () => {
          // disable the save button while waiting for response
          scope.isMarkAnimationSettingBusy = true;

          this.userSettingsService.updateMarkAnimationSetting(
            siteUser.id,
            scope.userSettings.markAnimationSetting
          ).then((result: IResult) => {
            this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
            scope.origMarkAnimationSetting = scope.userSettings.markAnimationSetting;
            scope.isMarkAnimationSettingBusy = false;
          }).catch((error: IError) =>  {
            scope.isMarkAnimationSettingBusy = false;
            this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
          });
        };

        // the save button is disabled if the current value of the checkbox is the same as what's saved in server
        scope.isSaveMarkAnimationSettingDisabled = () => {
          return scope.userHasNoSiteId() || scope.origMarkAnimationSetting === scope.userSettings.markAnimationSetting;
        };
      }
    }
  }

  private updateUserSettingsOnScope(userSettings: IUserSettings) {
    this.scope.userSettings = userSettings || {};
    this.scope.updateUserStartPage(this.scope.userSettings.startPage);
  }

  private onAppStateChanged() {
    const userSettingsState = appStore.getState().userSettings;
    const domainMap = userSettingsState[this.scope.serverUser.domainName];
    if (domainMap) {
      const userSettings = domainMap[this.scope.serverUser.username];
      if (userSettings !== this.scope.userSettings) {
        this.updateUserSettingsOnScope(userSettings);
      }
    }
  }

  private setupUserSettingsChangeListener() {
    const unsubscribe = appStore.subscribe(this.boundOnAppStateChanged);
    this.scope.$on('$destroy', () => {
      unsubscribe();
      this.userSettingsActions.forgetUserSettings(this.scope.serverUser.username, this.scope.serverUser.domainName);
    });
  }

}

export module UserSettingsCtrlCommon {

  export interface Scope extends ng.IScope {
    disableNameAndEmailSaveButton: () => boolean;
    serverUser: IServerUserInfo;
    isDomainFamilyAD: boolean;
    isDomainFamilyExternal: boolean;
    isDomainFamilyLocal: boolean;
    isLocalAuth: boolean;
    isViewerSelf: boolean;
    isViewerAdmin: boolean;
    isViewerServerAdmin: boolean;
    userSettings: IUserSettings;
    startPageText: string;
    startPageHref: string;
    welcomeChannelEnabled: boolean;
    updateWelcomeChannelEnabled(): void;
    updateWelcomeChannelEnabledBusy: boolean;
    initialWelcomeChannelEnabled: boolean;
    isWelcomeChannelEnabledPristine(): boolean;
    userSettingsUpdated(): void;
    onAfterStartPageReset(): void;
    changePasswordUrl: string;
    saveChangesToEmailAndDisplayName(): void;
    updateDisplayNameAllowed: boolean;
    updateUserStartPage(startPage: string): void;
    controls: {
      editDisplayNameControls: IEditDisplayNameControls;
      editEmailControls: IEditEmailControls;
    };
    delayUpdateEmailRequest: boolean;
    isNotificationEnabledForServer: boolean;
    isNotificationEnabledForSite: boolean;
    siteUser: ISiteUserInfo;
    userHasNoSiteId(): boolean;
    origNotificationSetting: boolean;
    isUpdateNotificationSettingDisabled(): boolean;
    updateNotificationSetting(): void;
    updateNotificationSettingBusy: boolean;
    server: ServerService;
    isSuspendedSubscriptionVisible(): boolean;
    suspendedSubscriptionSetting: boolean;
    updateSuspendedSubscriptionSetting(): void;
    origSuspendedSubscriptionSetting: boolean;
    isSuspendedSubscriptionSettingBusy: boolean;
    isSaveSuspendedSubscriptionSettingDisabled(): boolean;
    isSuspendedDataAlertVisible(): boolean;
    suspendedDataAlertSetting: boolean;
    updateSuspendedDataAlertSetting(): void;
    origSuspendedDataAlertSetting: boolean;
    isSuspendedDataAlertSettingBusy: boolean;
    isSaveSuspendedDataAlertSettingDisabled(): boolean;
    isMarkAnimationVisible(): boolean;
    markAnimationSetting: boolean;
    updateMarkAnimationSetting(): void;
    origMarkAnimationSetting: boolean;
    isMarkAnimationSettingBusy: boolean;
    isSaveMarkAnimationSettingDisabled(): boolean;
  }

}

Module.instance().service('UserSettingsCtrlCommon', UserSettingsCtrlCommon);
